import { createApi } from '@reduxjs/toolkit/query/react';
import baseApi from './base';
import paginationParams from 'utils/pagination-params';
import { setBreadcrumbs } from 'slices/config';

export const list = 'LIST_REQUESTS';
export const single = 'SINGLE_REQUEST';

export const talentDiscoveryApi = createApi({
  reducerPath: 'talentDiscoveryApi',
  baseQuery: baseApi,
  tagTypes: [list, single],
  endpoints: (builder) => ({
    submitHiringForm: builder.mutation({
      query: (data) => ({
        url: '/talent-discovery-requests/submit',
        method: 'POST',
        body: data
      }),
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        try {
          const { data: created } = await queryFulfilled;
          dispatch(
            talentDiscoveryApi.util.updateQueryData('listClassRequests', undefined, (draft) => {
              draft.data.items.unshift(created.data);
            })
          );
        } catch (err) {
          console.error('Error updating cache:', err);
        }
      }
    }),
    listClassRequests: builder.query({
      query: (params) => ({
        url: '/talent-discovery-requests/list',
        params
      }),
      providesTags: [list],
      ...paginationParams
    }),
    exportClassRequests: builder.query({
      query: (params) => ({
        url: '/talent-discovery-requests/export',
        params: { ...params },
        responseHandler: (response) => response.text()
      })
    }),
    getClassRequest: builder.query({
      query: (id) => ({
        url: `/talent-discovery-requests/view/${id}`
      }),
      providesTags: [single],
      async onQueryStarted(_, { dispatch, getState, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          if (getState().auth?.isLoggedIn) {
            dispatch(setBreadcrumbs(`${data?.data?.positionTitle ?? 'N/A'}`));
          }
        } catch (err) {
          console.error('Error updating cache:', err);
        }
      }
    }),
    updateClassRequest: builder.mutation({
      query: ({ id, ...data }) => ({
        url: `/talent-discovery-requests/update/${id}`,
        method: 'PUT',
        body: data
      }),
      async onQueryStarted({ id }, { dispatch, queryFulfilled }) {
        try {
          const { data: updated } = await queryFulfilled;
          dispatch(
            talentDiscoveryApi.util.updateQueryData('listClassRequests', undefined, (draft) => {
              const item = draft.data.items.find((item) => item.id === id);
              if (item) {
                Object.assign(item, updated.data);
              }
            })
          );
        } catch (err) {
          console.error('Error updating cache:', err);
        }
      },
      invalidatesTags: [single]
    }),
    deleteClassRequest: builder.mutation({
      query: (id) => ({
        url: `/talent-discovery-requests/delete/${id}`,
        method: 'DELETE'
      }),
      async onQueryStarted(id, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(
            talentDiscoveryApi.util.updateQueryData('listClassRequests', undefined, (draft) => {
              const idx = draft.data.items.findIndex((item) => item.id === id);
              if (idx > -1) {
                draft.data.items.splice(idx, 1);
              }
            })
          );
        } catch (err) {
          console.error('Error updating cache:', err);
        }
      }
    }),
    markAsCurrentVersion: builder.mutation({
      query: ({ id, versionId }) => ({
        url: `/talent-discovery-requests/versions/mark-as-current`,
        method: 'PUT',
        body: { classRequestId: id, id: versionId }
      }),
      invalidatesTags: [single]
    }),
    deleteRequestVersion: builder.mutation({
      query: ({ id, versionId }) => ({
        url: `/talent-discovery-requests/versions/delete/${id}/${versionId}`,
        method: 'DELETE'
      }),
      invalidatesTags: [single]
    })
  })
});

export const {
  useSubmitHiringFormMutation,
  useListClassRequestsQuery,
  useLazyExportClassRequestsQuery,
  useGetClassRequestQuery,
  useUpdateClassRequestMutation,
  useDeleteClassRequestMutation,
  useMarkAsCurrentVersionMutation,
  useDeleteRequestVersionMutation
} = talentDiscoveryApi;
